import styled, { css, DefaultTheme } from 'styled-components';
import { TokenName } from '@mewssystems/design-tokens';
import { getTextTokenPreset, getToken, Size, Sized, Variable } from '@optimus-web/common';
import { ANIMATION_CURVE, ANIMATION_DURATION, ButtonVariant, SPACING } from '@optimus-web/local-tokens';
import {
    getActiveBackgroundColor,
    getActiveBorderColor,
    getActiveColor,
    getBackgroundColor,
    getBorderColor,
    getColor,
    getCounterActiveBackgroundColor,
    getCounterActiveColor,
    getCounterBackgroundColor,
    getCounterColor,
    getCounterDisabledBackgroundColor,
    getCounterDisabledColor,
    getCounterHoverBackgroundColor,
    getCounterHoverColor,
    getDisabledBackgroundColor,
    getDisabledBorderColor,
    getDisabledColor,
    getHoverBackgroundColor,
    getHoverBorderColor,
    getHoverColor,
} from './getButtonStyles';

interface ButtonElementProps extends Required<Sized>, Variable<ButtonVariant> {
    wide?: boolean;
}

export const getButtonHeight = ({ size, theme }: { size: Size; theme: DefaultTheme }) => {
    switch (size) {
        case 'small':
            return getToken(TokenName.SIZING_400)({ theme });
        case 'medium':
            return getToken(TokenName.SIZING_500)({ theme });
        case 'large':
            return getToken(TokenName.SIZING_600)({ theme });
    }
};

export const getButtonPadding = ({ size, theme }: { size: Size; theme: DefaultTheme }) => {
    switch (size) {
        case 'small':
            return getToken(TokenName.SPACING_150)({ theme });
        case 'medium':
            return getToken(TokenName.SPACING_200)({ theme });
        case 'large':
            return getToken(TokenName.SPACING_300)({ theme });
    }
};

const COLOR_TRANSITION = css`
    transition-property: background-color, border-color, color;
    transition-timing-function: ${ANIMATION_CURVE.default};
    transition-duration: ${ANIMATION_DURATION.fast}ms;
`;

export const CounterElement = styled.div<Variable<ButtonVariant>>`
    text-align: center;
    padding: 0 ${getToken(TokenName.SPACING_50)}px;
    height: ${getToken(TokenName.SIZING_200)}px;
    box-sizing: border-box;
    min-width: ${getToken(TokenName.SIZING_200)}px;
    border-radius: 100px;
    flex-shrink: ${getToken(TokenName.SPACING_0)};
    ${getTextTokenPreset('BODY_XS_STRONG')};
    line-height: ${getToken(TokenName.SIZING_200)}px;
    background-color: ${(props) => getCounterBackgroundColor(props.variant, props)};
    color: ${(props) => getCounterColor(props.variant, props)};
    ${COLOR_TRANSITION};
`;

export const ButtonElement = styled.button<ButtonElementProps>`
    background-color: ${(props) => getBackgroundColor(props.variant, props)};
    color: ${(props) => getColor(props.variant, props)};
    border: ${getToken(TokenName.BORDER_WIDTH_150)}px solid ${(props) => getBorderColor(props.variant, props)};
    border-radius: ${getToken(TokenName.BORDER_RADIUS_100)}px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    ${(props) => (props.wide ? 'width: 100%;' : null)};
    ${({ size, theme }) =>
        size === 'small'
            ? getTextTokenPreset('BODY_M_STRONG')({ theme })
            : getTextTokenPreset('BODY_L_STRONG')({ theme })}
    padding: 0 ${(props) => getButtonPadding(props)}px;
    height: ${(props) => getButtonHeight(props)}px;
    ${COLOR_TRANSITION};
    white-space: nowrap;
    cursor: pointer;

    & > *:not(:last-child) {
        margin-right: ${SPACING[100]}px;
    }

    &:focus-visible {
        box-shadow: ${getToken(TokenName.FOCUS_OFFSET)};
        background-color: ${(props) =>
            props.variant === 'ghost' && getToken(TokenName.BACKGROUND_INTERACTIVE_NEUTRAL_SUBTLE_HOVER)};
        outline: none;
    }

    &:disabled {
        background-color: ${(props) => getDisabledBackgroundColor(props.variant, props)};
        color: ${(props) => getDisabledColor(props.variant, props)};
        border-color: ${(props) => getDisabledBorderColor(props.variant, props)};
        cursor: not-allowed;
        pointer-events: none;

        ${CounterElement} {
            background-color: ${(props) => getCounterDisabledBackgroundColor(props.variant, props)};
            color: ${(props) => getCounterDisabledColor(props.variant, props)};
        }
    }

    &:hover:not([disabled]) {
        background-color: ${(props) => getHoverBackgroundColor(props.variant, props)};
        color: ${(props) => getHoverColor(props.variant, props)};
        border-color: ${(props) => getHoverBorderColor(props.variant, props)};

        ${CounterElement} {
            background-color: ${(props) => getCounterHoverBackgroundColor(props.variant, props)};
            color: ${(props) => getCounterHoverColor(props.variant, props)};
        }
    }

    &:active:enabled {
        background-color: ${(props) => getActiveBackgroundColor(props.variant, props)};
        color: ${(props) => getActiveColor(props.variant, props)};
        border-color: ${(props) => getActiveBorderColor(props.variant, props)};

        ${CounterElement} {
            background-color: ${(props) => getCounterActiveBackgroundColor(props.variant, props)};
            color: ${(props) => getCounterActiveColor(props.variant, props)};
        }
    }
`;
